import {
  all, fork, put, takeEvery, call, select,
} from 'redux-saga/effects';
import {
  setInitialNodeNamesAndSlotNames,
  structuredClone,
  translateNodesAndEdgesToLegacyFormat,
} from './utils';

import { toast } from 'react-toastify';

import {
  RELINK_PHONENUMBER,
  REMOVE_DIALOG,
  REQUEST_DIALOG,
  REQUEST_VALIDATION_GRAPH,
  UNLINK_PHONENUMBER,
  UPDATE_DIALOG,
} from '../actions';
import {
  requestDialogSuccess,
  requestDialogError,
  updateDialogSuccess,
  updateDialogError,
  phonenumberRelinkSuccess,
  unlinkPhonenumberSuccess,
  removeDialogSuccess,
  phonenumberRelinkError,
  updateDialogWarning, requestValidationGraphSuccess,
} from './actions';
import { cc50Api } from '../../api';
import { getDialog, getGraph } from './selectors';
import { toCamelCaseDeep } from '../../utils';

// region Validation
const validationGraphRequest = async (graph) => cc50Api
  .post('/dialogs/validate_graph', {
    graph: JSON.stringify(graph),
  });

function* requestValidationGraph({ payload }) {
  const { dialogID } = payload;
  try {
    const graph = structuredClone(yield select(getGraph, dialogID) || {});
    if (!Object.keys(graph).length) return;
    const { status, data, error } = yield call(
      validationGraphRequest, graph,
    );
    if (status === 200) {
      const { graphValidation } = toCamelCaseDeep(data);
      yield put(requestValidationGraphSuccess(
        { dialogID, validationGraph: graphValidation },
      ));
      return;
    }
    console.error(error);
  } catch (error) {
    console.log(error);
  }
}

function* watchRequestValidationGraph() {
  return yield takeEvery(REQUEST_VALIDATION_GRAPH, requestValidationGraph);
}
// endregion

// region Dialog
const dialogRequest = async (domainID, dialogID, forceShowCurrent) => cc50Api
  .get('/dialogs/graph', {
    params: {
      domain_id: domainID,
      dialog_id: dialogID,
      latest_version: forceShowCurrent,
    },
  });

function* requestDialog({ payload }) {
  const { domainID, dialogID, forceShowCurrent } = payload;
  try {
    const { status, data, error } = yield call(
      dialogRequest, domainID, dialogID, forceShowCurrent,
    );
    if (status === 200) {
      const {
        dialog_id: dialogID,
        contains_personal_data: containsPersonalData,
        id: graphID,
        dialog_name: dialogName,
        created,
        serialized_at: serializedAt,
        graph,
        is_active: isActive,
        is_latest: isLatest,
        is_linked: isLinked,
        is_used_model: isUsedModel,
        has_changes: hasChanges,
      } = data;
      const result = {
        dialogID,
        dialogName,
        containsPersonalData,
        created,
        serializedAt,
        graph: {
          ...graph,
          graphID,
        },
        isActive,
        isLatest,
        isLinked,
        isUsedModel,
        hasChanges,
      };
      yield put(requestDialogSuccess(result));
    } else {
      yield put(requestDialogError(error.message));
    }
  } catch (error) {
    yield put(requestDialogError(error.message));
  }
}

function* watchRequestDialog() {
  return yield takeEvery(REQUEST_DIALOG, requestDialog);
}

const dialogUpdate = async (domainID, dialogID, graph, dialogName, containsPersonalData) => cc50Api
  .post('/dialogs/save', {
    domain_id: domainID,
    dialog_id: dialogID,
    graph: JSON.stringify(graph),
    name: dialogName,
    contains_personal_data: containsPersonalData,
  });

function* updateDialog({ payload }) {
  const { domainID, dialogID, dialogName, containsPersonalData } = payload;
  try {
    const dialog = structuredClone(yield select(getDialog, dialogID) || {});

    const [filteredNodes, filteredEdges] = translateNodesAndEdgesToLegacyFormat(
      dialog.graph.nodes, dialog.graph.edges,
    );
    dialog.graph.nodes = filteredNodes;
    dialog.graph.edges = filteredEdges;

    dialog.graph.nodes = setInitialNodeNamesAndSlotNames(
      dialog.graph.nodes,
      dialogID,
      dialogName,
    );

    const { status, response } = yield call(
      dialogUpdate, domainID, dialogID, dialog?.graph, dialogName, containsPersonalData
    );

    if (status === 200 || status === 204) {
      yield put(updateDialogSuccess(dialogID));
      toast.success(
        `Der Dialog "${dialogName}" wurde erfolgreich gespeichert.`,
      );
      yield call(requestValidationGraph, { payload: { dialogID } });
    } else {
      if (response?.status === 422) {
        yield put(updateDialogWarning());
        toast.warning(
          `Der Dialog "${dialogName}" wurde erfolgreich gespeichert, konnte jedoch nicht aktiviert werden. 
          Bitte den Dialog auf Vollständigkeit überprüfen`,
        );
        yield call(requestValidationGraph, { payload: { dialogID } });
        return;
      }
      yield put(updateDialogError(response?.data));
      toast.error(`Der Dialog "${dialogName}" konnte nicht gespeichert werden. ${response?.data}`);
      yield call(requestValidationGraph, { payload: { dialogID } });
    }
  } catch (error) {
    if (error.response?.status === 422) {
      yield put(updateDialogWarning());
      toast.warning(
        `Der Dialog "${dialogName}" wurde erfolgreich gespeichert, konnte jedoch nicht aktiviert werden. 
        Bitte den Dialog auf Vollständigkeit überprüfen`,
      );
      yield call(requestValidationGraph, { payload: { dialogID } });
      return;
    }
    yield put(updateDialogError(error?.message));
    toast.error(`Der Dialog "${dialogName}" konnte nicht gespeichert werden. ${error?.message}`);
    yield call(requestValidationGraph, { payload: { dialogID } });
  }
}

function* watchUpdateDialog() {
  return yield takeEvery(UPDATE_DIALOG, updateDialog);
}

const dialogRemove = async (domainID, dialogID) => cc50Api
  .post('/dialogs/remove', {
    domain_id: domainID,
    dialog_id: dialogID,
  });

function* removeDialog({ payload }) {
  const { domainID, dialogID } = payload;
  try {
    const { status, data, error } = yield call(
      dialogRemove, domainID, dialogID,
    );

    if (status === 204) {
      yield put(removeDialogSuccess(dialogID));
      toast.success('Dialog wurde erfolgreich gelöscht');
    } else {
      toast.error(
        'Beim Löschen des Dialogs ist ein Fehler aufgetreten!',
      );
    }
  } catch (error) {
    console.log(error);
    toast.error(
      'Beim Löschen des Dialogs ist ein Fehler aufgetreten!',
    );
  }
}

function* watchRemoveDialog() {
  return yield takeEvery(REMOVE_DIALOG, removeDialog);
}
// endregion

// region Phone
const phonenumberRelink = async (domainID, dialogID, phonenumberID) => cc50Api
  .post('/dialogs/relink', {
    domain_id: domainID,
    dialog_id: dialogID,
    phonenumber_id: phonenumberID,
  });

function* relinkPhonenumber({ payload }) {
  const { domainID, dialogID, phonenumberID } = payload;
  try {
    const { status, data, error } = yield call(
      phonenumberRelink, domainID, dialogID, phonenumberID,
    );

    if (status === 204) {
      yield put(phonenumberRelinkSuccess(dialogID));
      toast.success('Dialog wurde erfolgreich verknüpft');
    } else {
      toast.error(
        'Beim Verknüpfen des Dialogs ist ein Fehler aufgetreten!',
      );
    }
  } catch (error) {
    console.log(error);
    toast.error(
      'Beim Verknüpfen des Dialogs ist ein Fehler aufgetreten!',
    );
  }
}

function* watchRelinkPhonenumber() {
  return yield takeEvery(RELINK_PHONENUMBER, relinkPhonenumber);
}

const phonenumberUnlink = async (domainID, dialogID, phonenumberID) => cc50Api
  .post('/dialogs/unlink', {
    domain_id: domainID,
    dialog_id: dialogID,
    phonenumber_id: phonenumberID,
  });

function* unlinkPhonenumber({ payload }) {
  const { domainID, dialogID, phonenumberID } = payload;
  try {
    const { status } = yield call(
      phonenumberUnlink, domainID, dialogID, phonenumberID,
    );
    if (status === 204) {
      yield put(unlinkPhonenumberSuccess());
      toast.success('Dialog wurde erfolgreich entknüpft');
    } else {
      yield put(phonenumberRelinkError());
      toast.error(
        'Beim Entknüpfen des Dialogs ist ein Fehler aufgetreten!',
      );
    }
  } catch (error) {
    console.log(error);
    toast.error(
      'Beim Entknüpfen des Dialogs ist ein Fehler aufgetreten!',
    );
  }
}

function* watchUnlinkPhonenumber() {
  return yield takeEvery(UNLINK_PHONENUMBER, unlinkPhonenumber);
}
// endregion

export default function* rootSaga() {
  yield all([
    fork(watchRequestDialog),
    fork(watchUpdateDialog),
    fork(watchRelinkPhonenumber),
    fork(watchUnlinkPhonenumber),
    fork(watchRemoveDialog),
    fork(watchRequestValidationGraph),
  ]);
}
